/**
 * Language-code helpers shared by the interface locale (Lingui) and the
 * transcript translation target. A single picker (`display_language`) drives
 * both, so codes flow through these utilities to reconcile the two spaces:
 *
 * - The transcript target can be any language the backend translates to
 *   (`de`, `fr`, `tr`, …).
 * - The UI only has hand-written catalogs for `UI_CATALOGS`; anything else
 *   falls back to English.
 */

/**
 * Locales we ship a Lingui catalog for. `en` is the source locale. Mirrors
 * `backend/config/languages.py` SUPPORTED_LANGUAGES: every language offered in
 * the picker translates the UI, so nothing selectable falls back to English.
 */
export const UI_CATALOGS = [
  "en",
  "de",
  "fr",
  "es",
  "it",
  "pl",
  "nl",
  "pt",
  "tr",
  "ro",
] as const;

export type UiCatalog = (typeof UI_CATALOGS)[number];

/** Lower-cased primary subtag, e.g. `de-AT` → `de`. */
export function baseLang(code: string): string {
  return code.split("-")[0]?.toLowerCase() ?? "";
}

/** True when two codes share a base language (`de-AT` vs `de-DE`). */
export function sameBaseLanguage(a: string, b: string): boolean {
  return baseLang(a) === baseLang(b);
}

/**
 * Endonym for a language code — the language's own name for itself, e.g. `fr`
 * → "français", `de-DE` → "Deutsch (Deutschland)". Sourced from the platform's
 * ICU data via `Intl.DisplayNames`, so there is no name table to maintain.
 *
 * Returns null when ICU has no name for the code (`.of()` echoes the input) or
 * the tag is malformed (`Intl.DisplayNames` throws a RangeError).
 */
export function nativeLanguageName(code: string): string | null {
  try {
    const name = new Intl.DisplayNames([code], { type: "language" }).of(code);
    return name && name !== code ? name : null;
  } catch {
    return null;
  }
}

/**
 * Map an arbitrary language code to the UI catalog that should render it:
 * exact match → same-base German → English.
 */
export function toCatalog(code: string): UiCatalog {
  const exact = UI_CATALOGS.find((c) => c === code);
  if (exact) return exact;

  const sameBase = UI_CATALOGS.find((c) => sameBaseLanguage(c, code));
  if (sameBase) return sameBase;

  return "en";
}

/** Canonical UI catalog default for a German-primary product. */
const DEFAULT_CATALOG: UiCatalog = "de";

/**
 * Best UI catalog for the browser's preferred languages. Prefers an exact
 * match, then a same-base catalog (so a `de-AT` browser gets `de`), then the
 * German default.
 */
export function detectLocale(navLangs: readonly string[]): UiCatalog {
  for (const lang of navLangs) {
    const exact = UI_CATALOGS.find(
      (c) => c.toLowerCase() === lang.toLowerCase(),
    );
    if (exact) return exact;

    const sameBase = UI_CATALOGS.find((c) => sameBaseLanguage(c, lang));
    if (sameBase) return sameBase;
  }
  return DEFAULT_CATALOG;
}
